import {
  clearGuestDailyAttempt,
  getGuestDailyAttemptRecord,
} from './dailyGameStorage';
import {
  ensureDailyAttemptDb,
  getDailyAttemptDb,
  saveDailyAttemptDb,
} from './dailyGameStorageDb';

const MIGRATION_MAX_ATTEMPTS = 3;

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const buildSeedGameStateFromAttempt = (attempt) => {
  const guesses = Array.isArray(attempt?.guesses) ? attempt.guesses : [];
  const currentGuess = attempt?.gameOver ? '' : String(attempt?.currentGuess || '');
  const currentRow = Number.isFinite(Number(attempt?.currentRow))
    ? Math.max(0, Math.floor(Number(attempt.currentRow)))
    : 0;

  return {
    guesses,
    currentGuess,
    currentRow,
    gameOver: false,
  };
};

const tryMigrateGuestAttempt = async ({ userId, puzzleDateUtc, guestAttempt }) => {
  const existingAttempt = await getDailyAttemptDb({ userId, puzzleDateUtc });
  if (existingAttempt) {
    clearGuestDailyAttempt({ puzzleDateUtc });
    return {
      status: 'skipped',
      reason: 'user_attempt_exists',
      puzzleDateUtc,
      attempt: existingAttempt,
    };
  }

  await ensureDailyAttemptDb({
    userId,
    puzzleDateUtc,
    gameState: buildSeedGameStateFromAttempt(guestAttempt),
  });

  const didSave = await saveDailyAttemptDb({
    userId,
    puzzleDateUtc,
    attempt: guestAttempt,
  });

  const latestAttempt = await getDailyAttemptDb({ userId, puzzleDateUtc });
  if (didSave || latestAttempt) {
    clearGuestDailyAttempt({ puzzleDateUtc });
    return {
      status: 'migrated',
      reason: didSave ? 'saved' : 'loaded_after_seed',
      puzzleDateUtc,
      attempt: latestAttempt || guestAttempt,
    };
  }

  return {
    status: 'failed',
    reason: 'save_failed',
    puzzleDateUtc,
    attempt: null,
  };
};

export const migrateGuestDailyAttemptToUser = async ({ userId } = {}) => {
  if (!userId) {
    return {
      status: 'skipped',
      reason: 'missing_user_id',
      puzzleDateUtc: null,
      attempt: null,
    };
  }

  const guestRecord = getGuestDailyAttemptRecord();
  if (!guestRecord?.puzzleDateUtc || !guestRecord?.attempt) {
    return {
      status: 'skipped',
      reason: 'no_guest_attempt',
      puzzleDateUtc: null,
      attempt: null,
    };
  }

  const { puzzleDateUtc, attempt: guestAttempt } = guestRecord;
  let lastResult = {
    status: 'failed',
    reason: 'save_failed',
    puzzleDateUtc,
    attempt: null,
  };

  for (let attemptIndex = 1; attemptIndex <= MIGRATION_MAX_ATTEMPTS; attemptIndex += 1) {
    lastResult = await tryMigrateGuestAttempt({ userId, puzzleDateUtc, guestAttempt });
    if (lastResult.status === 'migrated' || lastResult.status === 'skipped') {
      return lastResult;
    }
    if (attemptIndex < MIGRATION_MAX_ATTEMPTS) {
      await wait(250 * attemptIndex);
    }
  }

  return lastResult;
};
